import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEmmaContext } from '../../../contexts/EmmaContext';
import { CompactView } from './CompactView';
import { ExpandedView } from './ExpandedView';
import { CollapsedTab } from './CollapsedTab';
import type { ViewState, LayoutMode } from '../../../types/emma-persistent.types';
import './persistent-interface.css';

interface PersistentEmmaInterfaceProps {
  layout?: 'split' | 'sidebar' | 'bottom-sheet';
  width?: number;
  position?: 'right' | 'left';
}

/**
 * Story 24.35: Persistent Emma Interface
 * Main container managing responsive layouts and mobile gestures
 */
export const PersistentEmmaInterface: React.FC<PersistentEmmaInterfaceProps> = ({ 
  layout: initialLayout,
  width,
  position 
}) => {
  const { emmaState, updatePersistentInterface } = useEmmaContext();
  const persistentInterface = emmaState.persistentInterface;
  const [viewState, setViewState] = useState<ViewState>('expanded');
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(initialLayout || 'split');
  const [touchStart, setTouchStart] = useState<number>(0);
  const mouseStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef<boolean>(false);

  // Sync with context for auto-expand - use persistent interface viewState if available
  useEffect(() => {
    if (persistentInterface?.viewState) {
      if (persistentInterface.viewState !== viewState) {
        setViewState(persistentInterface.viewState);
      }
    }
  }, [persistentInterface?.viewState]);

  // Sync layout prop from parent
  useEffect(() => {
    if (initialLayout) {
      setLayoutMode(initialLayout);
      // Set collapsed state for mobile bottom sheet
      if (initialLayout === 'bottom-sheet') {
        setViewState('collapsed');
      }
    }
  }, [initialLayout]);

  // Detect screen size and set layout mode (only if not controlled by parent via props?)
  // Actually, if parent passes initialLayout, we might still want to be responsive if parent doesn't handle it?
  // EmmaFirstLayout handles resizing window and switching layout mode passed as prop?
  // EmmaFirstLayout:
  // if (width < 768) setLayout('bottom-sheet')...
  // <PersistentEmmaInterface layout={layout} ... />
  // So parent controls layoutMode!
  
  // But PersistentEmmaInterface ALSO has a local effect for resize?
  // This seems redundant or conflicting.
  useEffect(() => {
    const handleResize = () => {
      // Only self-manage if NO initialLayout provided? or always?
      // Since props can change, we should rely on props if provided.
      // But component has initialLayout prop.
      // If parent updates layout prop, 'initialLayout' changes, useEffect above updates layoutMode.
      // But this effect runs on window resize.
      // Let's keep it safe, but maybe check if valid.
      const width = window.innerWidth;
      if (width < 768) {
        setLayoutMode('bottom-sheet');
        setViewState('collapsed');
      } else if (width < 1024) {
        setLayoutMode('sidebar');
      } else {
        setLayoutMode('split');
      }
    };

    if (!initialLayout) {
       handleResize();
       window.addEventListener('resize', handleResize);
       return () => window.removeEventListener('resize', handleResize);
    }
  }, [initialLayout]);
  // Note: I modified this effect to only run if !initialLayout, to avoid conflict with parent.
  // Actually, if initialLayout is provided, the useEffect([initialLayout]) handles updates.
  // EmmaFirstLayout updates the prop on resize. So we are good.

  // Mobile gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEnd = e.changedTouches[0].clientY;
    const swipeDistance = touchStart - touchEnd;
    const threshold = 50; // 50px threshold

    if (Math.abs(swipeDistance) > threshold) {
      const newState = swipeDistance > 0 ? 'expanded' : 'collapsed';
      setViewState(newState);
      // Persist to context/localStorage
      updatePersistentInterface({ viewState: newState });
    }
  };

  // Document-level mouse handlers for drag gestures
  const handleDocumentMouseMove = useCallback((e: MouseEvent) => {
    // Track during drag
  }, []);

  const handleDocumentMouseUp = useCallback((e: MouseEvent) => {
    if (mouseStartRef.current !== null && isDraggingRef.current) {
      const dragDistance = mouseStartRef.current - e.clientY;
      const threshold = 50;

      if (Math.abs(dragDistance) > threshold) {
        const newState = dragDistance > 0 ? 'expanded' : 'collapsed';
        setViewState(newState);
        // Persist to context/localStorage
        updatePersistentInterface({ viewState: newState });
      }
    }
    mouseStartRef.current = null;
    isDraggingRef.current = false;
    document.removeEventListener('mousemove', handleDocumentMouseMove);
    document.removeEventListener('mouseup', handleDocumentMouseUp);
  }, [updatePersistentInterface]);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseStartRef.current = e.clientY;
    isDraggingRef.current = true;
    // Add document-level listeners to catch mouseup even outside the element
    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Local mousemove - document listener handles this
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // Local mouseup - document listener handles this
  };

  const handleCollapse = () => {
    const newState = layoutMode === 'bottom-sheet' ? 'collapsed' : 'compact';
    setViewState(newState);
    // Persist to context/localStorage
    updatePersistentInterface({ viewState: newState });
  };

  const handleExpand = () => {
    setViewState('expanded');
    // Persist to context/localStorage
    updatePersistentInterface({ viewState: 'expanded' });
  };

  const handleTabClick = () => {
    setViewState('expanded');
    // Persist to context/localStorage
    updatePersistentInterface({ viewState: 'expanded' });
  };

  // Desktop/Tablet view
  if (layoutMode === 'split' || layoutMode === 'sidebar') {
    const containerClass = layoutMode === 'split' ? 'emma-split-view' : 'emma-sidebar';
    const containerStyle = {
      width: '100%',
      height: '100%',
      flex: '1 1 0%',
      minWidth: 0,
    };
    
    // 6C-5 (Feb 7): Disable pointer-events when compact to prevent click interception
    const pointerEvents = viewState === 'compact' ? 'none' : 'auto';
    
    return (
      <div 
        className={containerClass} 
        style={{ ...containerStyle, pointerEvents }}
      >
        {viewState === 'compact' ? (
          <CompactView onExpand={handleExpand} />
        ) : (
          <ExpandedView onCollapse={handleCollapse} />
        )}
      </div>
    );
  }

  // Mobile bottom sheet view
  // 6C-5 (Feb 7): Disable pointer-events when collapsed (except for collapsed tab itself)
  const sheetPointerEvents = viewState === 'collapsed' ? 'none' : 'auto';
  
  return (
    <div 
      className={`emma-bottom-sheet ${viewState === 'expanded' ? 'expanded' : ''}`}
      style={{ pointerEvents: sheetPointerEvents }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {viewState === 'collapsed' ? (
        <CollapsedTab 
          onClick={handleTabClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        />
      ) : (
        <ExpandedView onCollapse={handleCollapse} />
      )}
    </div>
  );
};
